import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getCurrentTimeZone, getTimeZoneLabel, saveCurrentTimeZone } from "./utils/timezones";
import { useAuth } from "./auth/AuthContext";
import { createPortal } from "react-dom";
import Menu from "./Modulos/Menu";
import ResponsiveBottomNav from "./components/responsive/ResponsiveBottomNav";
import ResponsiveModalSheets from "./components/responsive/ResponsiveModalSheets";
import { WindowManagerProvider } from "./context/WindowManagerContext";

const ACTIVE_STREAMS_STORAGE_KEY = "active_streams_v1";
const LAST_ACTIVITY_STORAGE_KEY = "auth:lastActivity";
const SESSION_CHANNEL_NAME = "infocarga-session";
const ACTIVITY_BROADCAST_THROTTLE_MS = 5000;

const AuthenticatedApp = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { accessToken: token, status, logout } = useAuth();
    const lastActionTime = useRef(Date.now());
    const logoutStarted = useRef(false);
    const lastActivityBroadcast = useRef(0);
    const sessionChannel = useRef(null);
    const intervalRefresh = localStorage.getItem("intervalRefresh");

    const { pathname } = location;

    const inactivityInterval = useMemo(() => {
        const configuredMinutes = Number(process.env.REACT_APP_INACTIVITY_INTERVAL);
        return Number.isFinite(configuredMinutes) && configuredMinutes > 0 ? configuredMinutes : null;
    }, []);

    const intervalTime = useMemo(() => {
        if (inactivityInterval !== null) {
            return inactivityInterval * 60 * 1000;
        }
        const sessionHours = Number(intervalRefresh);
        return Number.isFinite(sessionHours) && sessionHours > 0 ? sessionHours * 55 * 60 * 1000 : 55 * 60 * 1000;
    }, [inactivityInterval, intervalRefresh]);
    const isLoginPage = useMemo(() => location.pathname === "/" || location.pathname === "/login", [location.pathname]);
    // AuthenticatedApp solo envuelve rutas privadas, por lo que la politica de
    // inactividad se aplica a toda la sesion, independientemente de la pagina.
    const inactivityEnabled = status === "authenticated";

    const clearLiveStreamsCache = useCallback(() => {
        try {
            sessionStorage.removeItem(ACTIVE_STREAMS_STORAGE_KEY);
            localStorage.removeItem(ACTIVE_STREAMS_STORAGE_KEY);
        } catch (error) {
            console.error("Error limpiando cache de activeStreams:", error);
        }
    }, []);

    const handleUserActivity = useCallback(() => {
        if (!isLoginPage) {
            const now = Date.now();
            lastActionTime.current = now;
            if (now - lastActivityBroadcast.current >= ACTIVITY_BROADCAST_THROTTLE_MS) {
                lastActivityBroadcast.current = now;
                localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(now));
                sessionChannel.current?.postMessage({ type: "activity", timestamp: now });
            }
        }
    }, [isLoginPage]);

    useEffect(() => {
        const storedActivity = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
        if (Number.isFinite(storedActivity) && storedActivity > 0) {
            lastActionTime.current = storedActivity;
        }

        if (typeof BroadcastChannel !== "undefined") {
            const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
            sessionChannel.current = channel;
            channel.onmessage = ({ data }) => {
                if (data?.type === "activity" && Number.isFinite(data.timestamp)) {
                    lastActionTime.current = Math.max(lastActionTime.current, data.timestamp);
                }
            };
        }

        const handleStorage = (event) => {
            if (event.key !== LAST_ACTIVITY_STORAGE_KEY) return;
            const timestamp = Number(event.newValue);
            if (Number.isFinite(timestamp)) {
                lastActionTime.current = Math.max(lastActionTime.current, timestamp);
            }
        };
        window.addEventListener("storage", handleStorage);

        return () => {
            window.removeEventListener("storage", handleStorage);
            sessionChannel.current?.close();
            sessionChannel.current = null;
        };
    }, []);

    useEffect(() => {
        if (!inactivityEnabled) return;

        lastActionTime.current = Date.now();
        localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(lastActionTime.current));
        logoutStarted.current = false;

        const events = ["pointerdown", "keydown", "scroll", "touchstart"];
        events.forEach((event) => window.addEventListener(event, handleUserActivity, { passive: true }));

        return () => {
            events.forEach((event) => window.removeEventListener(event, handleUserActivity));
        };
    }, [handleUserActivity, inactivityEnabled]);

    useEffect(() => {
        if (!inactivityEnabled) return;

        const checkInactivity = setInterval(() => {
            const sharedActivity = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY));
            const effectiveLastActivity = Number.isFinite(sharedActivity)
                ? Math.max(lastActionTime.current, sharedActivity)
                : lastActionTime.current;
            const elapsedTime = Date.now() - effectiveLastActivity;

            if (elapsedTime >= intervalTime && !logoutStarted.current) {
                logoutStarted.current = true;
                console.warn(`Sesion cerrada por inactividad de ${Math.round(intervalTime / 60000)} minuto(s).`);
                clearLiveStreamsCache();
                logout().finally(() => {
                  if (location.pathname !== "/" && location.pathname !== "/login") {
                    navigate("/login", { replace: true });
                  }
                });
            }
        }, 1000);

        return () => clearInterval(checkInactivity);
    }, [navigate, intervalTime, inactivityEnabled, location.pathname, clearLiveStreamsCache, logout]);

    useEffect(() => {
        if (isLoginPage || !token) return;
        const shouldShowPrompt = sessionStorage.getItem("show_timezone_prompt") === "1";
        if (!shouldShowPrompt) return;

        sessionStorage.removeItem("show_timezone_prompt");

        const activeTimeZone = getCurrentTimeZone();
        const activeTimeZoneLabel = getTimeZoneLabel(activeTimeZone);
        const browserTimeZone = localStorage.getItem("browser_time_zone") || activeTimeZone;
        const browserTimeZoneLabel = getTimeZoneLabel(browserTimeZone);

        Swal.fire({
            icon: "info",
            title: "Zona horaria activa",
            html: `Detectada en navegador: <strong>${browserTimeZoneLabel}</strong><br>Zona activa en la app: <strong>${activeTimeZoneLabel}</strong>`,
            showCancelButton: true,
            confirmButtonText: "Cambiar zona horaria",
            cancelButtonText: "Mantener actual",
            timer: 15000,
            timerProgressBar: true,
            allowOutsideClick: true,
        }).then((result) => {
            if (result.isConfirmed) {
                saveCurrentTimeZone(browserTimeZone);
            }
        });
    }, [isLoginPage, token, navigate]);

    useEffect(() => {
        if (pathname === "/" || pathname === "/login") {
            return;
        }

        if (status === "loading") return;
        if (status === "anonymous" || !token) {
            clearLiveStreamsCache();
            navigate("/login", { replace: true });
        }
    }, [pathname, token, status, navigate, clearLiveStreamsCache]);

    if (status === "loading") return null;

    const menuContainer = document.getElementById('menu');

    return (
        <WindowManagerProvider>
            {status === 'authenticated' && menuContainer && createPortal(<Menu />, menuContainer)}
            {status === 'authenticated' && <ResponsiveModalSheets />}
            {children}
            {status === 'authenticated' && <ResponsiveBottomNav />}
        </WindowManagerProvider>
    );
};

export default AuthenticatedApp;
